/**
 * PromosLibrary — the Coupons & Promos rail surface. Verbatim port of the
 * prototype's PromosLibrary: Coupons | Promos tabs, list/grid, per-row actions
 * (edit / duplicate / pause / delete). Admin-authored, reusable across events.
 *
 * Route: `promos` · rail: Coupons & Promos · caps: promos.read / promos.write.
 */
import { useState } from "react";
import { Btn, Card, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, EmptyState, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useDiscounts } from "./useDiscounts";
import { discountValueLabel, type Discount } from "./promos-data";
import { DTypeGlyph, DTypePill } from "./promos-shared";
import { useT, t as i18nT } from "../../lib/i18n";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";

const dpTh = { textAlign: "left" as const, padding: "10px 16px", whiteSpace: "nowrap" as const };
const dpTd = { padding: "12px 16px", verticalAlign: "middle" as const };

export default function PromosLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useDiscounts();
  const [tab, setTab] = useState<"coupon" | "promo">("coupon");
  const [query, setQuery] = useState("");
  const [view, setView] = useViewMode("promos:view");
  const canWrite = can("promos.write");

  const counts = { coupon: store.ofType("coupon").length, promo: store.ofType("promo").length };
  /* Folder Library shell (family "promos") — mounted, not copied. One folder
     set spans both the Coupons and Promos tabs; the counts follow the active tab, because the
     list shows only that tab (CC follow-up 2026-10-15). */
  const lib = useFolderLibrary({
    family: "promos",
    ids: store.ofType(tab).map((d) => d.id),
    canWrite,
    allLabel: t("promos.library.allDiscounts", "All coupons & promos"),
    searchAllLabel: t("promos.library.searchAllDiscounts", "Search all coupons & promos"),
    query,
    failTitle: t("promos.library.couldnTUpdate", "Couldn't update"),
    onAfterPurge: () => store.reload?.(),
  });
  const poolSet = new Set(lib.pool);
  const rows = store.ofType(tab).filter((d) => poolSet.has(d.id) && (!query.trim() || `${d.name} ${d.code}`.toLowerCase().includes(query.toLowerCase())));

  // UIR-21: await the store's client write before any feedback; danger-toast the
  // API's error on rejection (the store no longer commits or swallows failures).
  const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));
  const togglePause = async (d: Discount) => {
    try { await store.setPaused(d.id, !d.paused); toast({ tone: d.paused ? "success" : "warn", title: d.paused ? "Resumed" : "Paused" }); }
    catch (e) { toast({ tone: "danger", title: t("promos.library.couldnTUpdate", "Couldn't update"), subtitle: errMsg(e) }); }
  };
  const dup = async (d: Discount) => {
    try { const c = await store.duplicate(d.id); toast({ tone: "success", title: t("promos.library.duplicated", "Duplicated"), subtitle: c ? `"${c.name}" added.` : undefined }); }
    catch (e) { toast({ tone: "danger", title: t("promos.library.couldnTDuplicate", "Couldn't duplicate"), subtitle: errMsg(e) }); }
  };
  const del = async (d: Discount) => {
    try { await store.remove(d.id); toast({ tone: "warn", title: t("promos.library.deleted", "Deleted") }); }
    catch (e) { toast({ tone: "danger", title: t("promos.library.couldnTDelete", "Couldn't delete"), subtitle: errMsg(e) }); }
  };
  const actions = (d: Discount) => ({
    onView: () => nav.go("promos.edit", { id: d.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("promos.edit", { id: d.id }) : undefined,
    onDuplicate: canWrite ? () => dup(d) : undefined,
    moreHeader: d.name,
    moreItems: canWrite ? [
      lib.foldersMenuItem(d.id),
      { icon: <Icon.clock size={14} />, label: d.paused ? "Resume" : "Pause", onClick: () => togglePause(d) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("common.actions.delete", "Delete"), danger: true, onClick: () => del(d) },
    ] : [],
  });

  const tabs = [
    { id: "coupon", label: t("promos.library.coupons", "Coupons"), count: counts.coupon, onClick: () => setTab("coupon") },
    { id: "promo", label: t("promos.library.promos", "Promos"), count: counts.promo, onClick: () => setTab("promo") },
  ];

  return (
    <Screen rail="promos" tabs={tabs} activeTab={tab}>
      <PageHeader
        title={t("promos.library.couponsPromos", "Coupons & Promos")}
        subtitle={t("promos.library.discountsSetUpByAdministratorsForUsersToApplyToEventsCoupons", "Discounts set up by administrators for users to apply to events. Coupons need a code; promos apply automatically.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("promos.edit", { new: 1, type: tab })}>{tab === "promo" ? t("promos.library.newPromo", "New promo") : t("promos.library.newCoupon", "New coupon")}</Btn> : null}
        </>}
      />
      <FolderShellFrame lib={lib}>
        <div style={{ padding: "10px 0 4px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Input full={false} leading={<Icon.search size={14} />} placeholder={tab === "coupon" ? t("promos.promosLibrary.findByNameOrCode", "Find by name or code…") : t("promos.promosLibrary.findAPromo", "Find a promo…")} value={query} onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 240 }} />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: T.soft }}>{rows.length} {t("promos.library.of", "of")}{" "}{counts[tab]}{store.serverTotal != null && store.serverTotal > store.all().length ? t("promos.library.text4", "· {serverTotal} {arg2}", { serverTotal: store.serverTotal, arg2: t("promos.library.onTheServer", "on the server — showing the first page") }) : ""}</span>
        </div>
        <FolderShellBars lib={lib}/>

        {rows.length === 0 ? (
          <EmptyState icon={<DTypeGlyph type={tab} size={26} />}
            title={t("promos.library.noTabPromoPromosCouponsMatch.icu", "No {kind, select, promo {promos} other {coupons}} match", { kind: tab })}
            body={t("promos.library.tryClearingFiltersOrCreateANewOne", "Try clearing filters, or create a new one.")}
            action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("promos.edit", { new: 1, type: tab })}>{tab === "promo" ? t("promos.library.newPromo", "New promo") : t("promos.library.newCoupon", "New coupon")}</Btn> : undefined} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
            {rows.map((d) => (
              <Card key={d.id} {...lib.dragProps(d.id)} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", borderLeft: `3px solid ${d.paused ? T.orange1 : T.green1}`, outline: lib.sel.has(d.id) ? `2px solid ${T.p1}` : "none" }}
                onClick={() => nav.go("promos.edit", canWrite ? { id: d.id } : { id: d.id, view: 1 })}>
                <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    {canWrite ? <input type="checkbox" checked={lib.sel.has(d.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => lib.toggleSel(d.id, e.target.checked)} style={{ accentColor: T.p1, marginTop: 8 }} /> : null}
                    <span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><DTypeGlyph type={d.type} size={16} /></span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{d.name || t("promos.library.text5", "Untitled")}</div>
                      <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{d.type === "coupon" ? <span style={{ fontFamily: T.fontMono }}>{d.code || t("promos.library.text6", "NO-CODE")}</span> : (d.audience || "Auto-applied")}</div>
                    </div>
                    {d.paused ? <Pill tone="warn">{t("promos.library.paused", "Paused")}</Pill> : null}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: T.n1 }}>{discountValueLabel(d)}</span>
                    {d.minSpend ? <span style={{ fontSize: 12, color: T.soft }}>{t("promos.library.min", "min $")}{d.minSpend}</span> : null}
                    <DTypePill type={d.type} />
                  </div>
                  <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 14, fontSize: 12, color: T.soft }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.check size={12} /> {d.uses}{d.maxUses ? t("promos.library.text7", "/ {maxUses}", { maxUses: d.maxUses }) : ""} {t("promos.library.used", "used")}</span>
                    {lib.folderChips(d.id)}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginLeft: "auto" }}><Icon.calendar size={12} /> {d.starts} → {d.ends}</span>
                  </div>
                </div>
                <CardActionBar {...actions(d)} />
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding: 0, marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  {canWrite ? <th style={{ ...dpTh, width: 34, paddingRight: 0 }}><input type="checkbox" aria-label={t("promos.library.ariaLabel", "Select all")} checked={rows.length > 0 && rows.every((r) => lib.sel.has(r.id))} onChange={(e) => lib.setSel(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} style={{ accentColor: T.p1 }} /></th> : null}
                  <th style={dpTh}>{tab === "coupon" ? t("promos.library.text2", "Coupon") : t("promos.library.text3", "Promo")}</th>
                  <th style={dpTh}>{t("promos.library.discount", "Discount")}</th>
                  <th style={dpTh}>{t("promos.library.usage", "Usage")}</th>
                  <th style={dpTh}>{t("promos.library.window", "Window")}</th>
                  <th style={{ ...dpTh, width: 140 }}>{t("promos.library.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => (
                  <tr key={d.id} {...lib.dragProps(d.id)} onClick={() => nav.go("promos.edit", canWrite ? { id: d.id } : { id: d.id, view: 1 })}
                    style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", borderLeft: `3px solid ${d.paused ? T.orange1 : T.green1}`, background: lib.sel.has(d.id) ? T.p4 : "transparent" }}>
                    {canWrite ? <td style={{ ...dpTd, width: 34, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={lib.sel.has(d.id)} onChange={(e) => lib.toggleSel(d.id, e.target.checked)} style={{ accentColor: T.p1 }} /></td> : null}
                    <td style={dpTd}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 30, height: 30, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><DTypeGlyph type={d.type} size={15} /></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: T.n1, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>{d.name || t("promos.library.text5", "Untitled")}{d.paused ? <Pill tone="warn">{t("promos.library.paused", "Paused")}</Pill> : null}{lib.folderChips(d.id)}</div>
                          <div style={{ color: T.soft, fontSize: 11.5 }}>{d.type === "coupon" ? <span style={{ fontFamily: T.fontMono }}>{d.code || t("promos.library.text6", "NO-CODE")}</span> : (d.audience || "Auto-applied")}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...dpTd, color: T.n1, fontWeight: 600 }}>{discountValueLabel(d)}{d.minSpend ? <span style={{ color: T.soft, fontWeight: 400 }}> {t("promos.library.minea8ea753", "· min $")}{d.minSpend}</span> : null}</td>
                    <td style={{ ...dpTd, color: T.soft }}>{d.uses}{d.maxUses ? t("promos.library.text7", "/ {maxUses}", { maxUses: d.maxUses }) : ""}</td>
                    <td style={{ ...dpTd, color: T.soft, fontSize: 12 }}>{d.starts} → {d.ends}</td>
                    <td style={dpTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(d)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <ExplainerFooter>
          <b>{i18nT("promos.library.adminAuthoredAmpReusable.rendered", "Admin-authored & reusable.")}</b> {t("promos.library.administratorsDefineACouponOrPromoOnceThenUsersAddItTo", "Administrators define a coupon or promo once, then users add it to any event — the same way booking questions work. Pause a discount to temporarily disable it without deleting it.")}</ExplainerFooter>
      </FolderShellFrame>
      <FolderShellOverlays lib={lib}/>
    </Screen>
  );
}
